import React from 'react';
import { TableCell, TableRow } from '@/components/ui/Table';
import { Button } from '@/components/ui/Buttons';
import Text from '@/components/ui/Text';
import I18n from '@/modules/i18n';
import cn from '@/utils/cn';
import stripStandardsAliasPrefix from '@/utils/stripStandardsAliasPrefix';
import CANDIDATE_COLUMN_WIDTHS from './columns';
import { CandidateStandard, candidateLabel } from '../types';

interface CandidateRowProps {
  candidate: CandidateStandard;
  disabled: boolean;
  isSelected: boolean;
  onAdd: (candidate: CandidateStandard, slot: number) => void;
  onToggleSelect: (tagId: number) => void;
  slot: number;
}

const ROW_CELL = 'tw-py-2 tw-align-top';

const CandidateRow = ({
  candidate, disabled, isSelected, onAdd, onToggleSelect, slot,
}: CandidateRowProps) => {
  const { description, standard } = candidate;
  const label = candidateLabel(candidate);
  const descriptionText = stripStandardsAliasPrefix(description, standard);

  return (
    <TableRow
      className={cn(
        'tw-border-b tw-border-solid tw-border-light-grey',
        'hover:tw-bg-light-grey focus-within:tw-bg-light-grey focus-within:tw-outline focus-within:tw-outline-2',
        'focus-within:tw-outline-offset-[-2px] focus-within:tw-outline-tci-purple',
      )}
    >
      <TableCell className={cn(CANDIDATE_COLUMN_WIDTHS.select, ROW_CELL, 'tw-pl-4 tw-text-center')}>
        <input
          aria-label={I18n.t('select_standard', { standard: label })}
          checked={isSelected}
          type="checkbox"
          onChange={() => onToggleSelect(candidate.tag_id)}
        />
      </TableCell>
      <TableCell className={cn(CANDIDATE_COLUMN_WIDTHS.standard, ROW_CELL)}>
        <Text className="tw-text-tci-black tw-break-words" size="sm" weight="semibold">{standard}</Text>
      </TableCell>
      <TableCell className={cn(CANDIDATE_COLUMN_WIDTHS.description, ROW_CELL)}>
        <Text className="tw-text-very-dark-grey" size="sm">{descriptionText}</Text>
      </TableCell>
      <TableCell className={cn(CANDIDATE_COLUMN_WIDTHS.add, ROW_CELL, 'tw-pr-4 tw-text-right')}>
        <Button
          outlineStyle
          small
          aria-label={I18n.t('add_standard', { standard: label })}
          className="tw-h-6"
          data-candidate-action={candidate.tag_id}
          disabled={disabled}
          onClick={() => onAdd(candidate, slot)}
        >
          {I18n.t('add')}
        </Button>
      </TableCell>
    </TableRow>
  );
};

// Memoized because search re-renders the whole list on every keystroke, and "Show more" keeps
// appending pages to it. Only effective while the callbacks stay stable.
export default React.memo(CandidateRow);
